import React, { useMemo, useState } from "react";
import { Box, Heading, Paragraph, ResponsiveContext, Skeleton, Text } from "grommet";
import styled from "styled-components";
import { useSelector } from "react-redux";

import { RootState } from "../store";
import { VulnerablePopulationMetricType } from "../models";
import {
  setIsWidgetInitialState,
  setSelectedVulnerablePopulations
} from "../actions/vulnerablePopulations";
import Pill from "./VulnerablePopWidgetPill";
import PlusIconSmall from "../icons/PlusIconSmall";
import { useDispatch } from "react-redux";
import GlossaryLink from "./GlossaryLink";

interface MessageProps {
  compact: boolean;
}

const Message = styled(Paragraph)<MessageProps>`
  background: var(--warm-gray-200);
  padding: ${props => (props.compact ? "0.2rem 0.3rem" : "0.6rem 0.9rem")};
  border-radius: var(--border-radius);
  font-size: ${props => (props.compact ? "11px" : "14px")};

  span {
    font-size: inherit;
  }
`;

export type PreviousTerm = {
  term: string | null;
  position: DOMRect | null;
};

const VulnerablePopWidgetExpanded = ({ showOther = true }: { showOther?: boolean }) => {
  const {
    selectedVulnerablePopulations,
    rankedVulnerablePopulations,
    selectedHighlightedTractIds,
    isWidgetInitialState
  } = useSelector((state: RootState) => state.vulnerablePopulations);
  const [previousTerm, setPreviousTerm] = useState<PreviousTerm>({ term: null, position: null });
  const [terms, setTerms] = useState<ReadonlyArray<VulnerablePopulationMetricType>>([]);

  const dispatch = useDispatch();

  // Populate terms list from vulnerable populations state
  useMemo(() => {
    rankedVulnerablePopulations.length > 0 &&
      setTerms(rankedVulnerablePopulations as ReadonlyArray<VulnerablePopulationMetricType>);
  }, [rankedVulnerablePopulations]);

  const setSelectedPopulations = (populations: ReadonlyArray<string>) => {
    dispatch(setSelectedVulnerablePopulations(populations));
  };

  const handleSelect = (newValue: VulnerablePopulationMetricType) => {
    selectedVulnerablePopulations.includes(newValue)
      ? setSelectedPopulations(selectedVulnerablePopulations.filter(val => val !== newValue))
      : setSelectedPopulations([...selectedVulnerablePopulations, newValue]);
    // Move the term that was just toggled to the end of the array
    setTerms([...terms.filter(term => term !== newValue), newValue]);
    dispatch(setIsWidgetInitialState(false));
  };

  return (
    <ResponsiveContext.Consumer>
      {size => (
        <Box>
          <Box
            pad={showOther ? "small" : ".5rem"}
            gap={showOther ? "small" : ".5rem"}
            role="group"
            a11yTitle="Highlighted categories"
            style={{
              borderBottom:
                size === "small" || !showOther ? undefined : "1px solid var(--warm-gray-300)"
            }}
          >
            <Text
              id="desc-highlighted"
              weight={700}
              size={showOther ? "1.4rem" : "1.1rem"}
              color="#000"
            >
              Highlight areas that rank above the local median for:
            </Text>
            {terms.length !== 0 ? (
              <Box
                direction="row"
                wrap={true}
                style={{
                  gap: ".5rem .25rem"
                }}
                align="center"
              >
                {terms
                  .filter(term => selectedVulnerablePopulations.includes(term))
                  // Inserts a "+" icon after each pill
                  .flatMap((term, index) => [
                    <Pill
                      term={term}
                      key={term}
                      selected={true}
                      previousTerm={previousTerm}
                      setPreviousTerm={setPreviousTerm}
                      handleClick={handleSelect}
                      compact={!showOther}
                    />,
                    <PlusIconSmall
                      key={index}
                      size={showOther ? "11" : "8"}
                      color="var(--text-700)"
                      aria-label="and"
                    />
                  ])
                  // Remove the trailing "+"
                  .slice(0, -1)}
              </Box>
            ) : (
              <Skeleton height="2.6rem" />
            )}
            {rankedVulnerablePopulations.length > 0 ? (
              selectedVulnerablePopulations.length === 0 ? (
                <Message role="status" compact={!showOther}>
                  Select one or more categories to see results
                </Message>
              ) : (
                selectedHighlightedTractIds.length === 0 && (
                  <Message role="status" compact={!showOther}>
                    <Text weight={600} color="black" style={{ display: "block" }}>
                      No areas meet these criteria
                    </Text>
                    Select fewer categories to see results
                  </Message>
                )
              )
            ) : (
              ""
            )}
          </Box>
          {showOther && (
            <Box pad="small" gap="small" role="group" aria-labelledby="categories">
              <Heading level={4} id="categories">
                Other categories
              </Heading>
              <Box direction="row" wrap={true} style={{ gap: ".5rem" }}>
                {terms.length !== 0 ? (
                  terms
                    .filter(term => !selectedVulnerablePopulations.includes(term))
                    .map((term, index) => (
                      <Pill
                        term={term}
                        key={term}
                        previousTerm={previousTerm}
                        setPreviousTerm={setPreviousTerm}
                        handleClick={handleSelect}
                      />
                    ))
                ) : (
                  <Skeleton height="8.8rem" />
                )}
              </Box>
              {/* In the initial state, show text explaining the selection criteria */}
              {isWidgetInitialState && (
                <Paragraph size="small">
                  Two{" "}
                  <GlossaryLink term="highest_ranked_categories">
                    highest-ranked categories
                  </GlossaryLink>{" "}
                  selected by default.
                </Paragraph>
              )}
            </Box>
          )}
        </Box>
      )}
    </ResponsiveContext.Consumer>
  );
};

export default VulnerablePopWidgetExpanded;
